import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  BranchPrintTemplateView,
  PrintDocumentType,
  PrintTemplate,
  PrintTemplateOverride,
  PrintTemplateView,
} from '@optiflow/types';
import { api, withBranch } from '@/services/apiClient';

const KEY = ['print-templates'] as const;
const branchKey = (branchId: string) => ['print-templates', 'branch', branchId] as const;

/** A document already read in this session carries the old layout; the next print must read it again. */
function forgetPrintedDocuments(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: ['sales', 'print'] });
}

export function usePrintTemplatesQuery() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<PrintTemplateView[]>('/print-templates'),
  });
}

/** Replaces one document type's template at the version the screen read; the other document type is never sent. */
export function useSavePrintTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentType,
      template,
      version,
    }: {
      documentType: PrintDocumentType;
      template: PrintTemplate;
      version: number;
    }) => api.put<PrintTemplateView>(`/print-templates/${documentType}`, { ...template, version }),
    onSuccess: () => {
      // Branch views are built on the tenant template, so they move with it.
      void queryClient.invalidateQueries({ queryKey: KEY });
      forgetPrintedDocuments(queryClient);
    },
  });
}

/** One branch's overrides for both document types; the branch is named for this request only, and the API checks the caller may act in it. */
export function useBranchPrintTemplatesQuery(branchId: string) {
  return useQuery({
    queryKey: branchKey(branchId),
    queryFn: () =>
      api.get<BranchPrintTemplateView[]>('/print-templates/branch', withBranch(branchId)),
  });
}

export function useSaveBranchOverrideMutation(branchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentType,
      overrides,
      version,
    }: {
      documentType: PrintDocumentType;
      overrides: PrintTemplateOverride;
      version: number;
    }) =>
      api.put<BranchPrintTemplateView>(
        `/print-templates/${documentType}/branch`,
        { overrides, version },
        withBranch(branchId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: branchKey(branchId) });
      forgetPrintedDocuments(queryClient);
    },
  });
}

export function useResetBranchOverrideMutation(branchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentType: PrintDocumentType) =>
      api.delete<BranchPrintTemplateView>(
        `/print-templates/${documentType}/branch`,
        withBranch(branchId),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: branchKey(branchId) });
      forgetPrintedDocuments(queryClient);
    },
  });
}
